import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { Markdown, ActionRow } from "@/components/ai/AtlasAI";
import { AtlasContextBar } from "@/components/ai/AtlasContextBar";
import { HistorySheet, ContextSheet } from "@/components/ai/AtlasSheets";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { addRecent } from "@/hooks/usePrefs";
import { Bot } from "lucide-react";
import { ArrowUp, ArrowUpRight, Loader2, ShieldCheck, HelpCircle, Mic, Copy } from "lucide-react";

function AssignedBadge({ a, onGo }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs rounded-xl px-3 py-2" style={{ background: "var(--ai-soft)" }} data-testid="ai-assigned-badge">
      <Bot className="w-3.5 h-3.5" style={{ color: "var(--ai)" }} /><span>Handed to <b>{a.name}</b>{a.role ? ` · ${a.role}` : ""} · manager {a.manager_name}</span>
      {a.status === "needs_approval" ? <button onClick={() => onGo("/decisions")} className="chip border-amber-300 text-amber-800 bg-amber-50 ml-auto" data-testid="ai-assignment-decision">Needs approval → Decisions</button> : <span className="chip border-teal-200 text-teal-700 bg-white ml-auto">Completed</span>}
    </div>);
}

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const STARTER = "What needs my attention today?";

function followUps(m) {
  const out = ["Why?", "What should I do first?"];
  const c = m.citations?.find((x) => x.label);
  if (c) out.push(`Tell me more about ${c.label}`);
  if (m.actions?.length) out.push("What happens if I do nothing?");
  return out.slice(0, 3);
}

export default function AtlasAIPage() {
  const { activeTenant, ctx } = useAtlas();
  const navigate = useNavigate();
  const location = useLocation();
  const [convId, setConvId] = useState(() => localStorage.getItem("atlas_conv") || null);
  const [messages, setMessages] = useState([]);
  const [convs, setConvs] = useState([]);
  const [input, setInput] = useState(STARTER);
  const [loading, setLoading] = useState(false);
  const [attention, setAttention] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [ground, setGround] = useState({});
  const [histOpen, setHistOpen] = useState(false);
  const [ctxOpen, setCtxOpen] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const firedRef = useRef(false);
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);
  const speechSupported = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  const toggleVoice = () => {
    if (!speechSupported) { toast.error("Voice input isn't supported in this browser. Try Chrome or Safari."); return; }
    if (listening) { recognitionRef.current?.stop(); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = "en-US"; rec.interimResults = true; rec.continuous = false;
    const base = !input || input === STARTER ? "" : input.trim() + " ";
    rec.onresult = (e) => { let txt = ""; for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript; setInput(base + txt); };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recognitionRef.current = rec;
    setListening(true);
    try { rec.start(); } catch { setListening(false); }
  };
  useEffect(() => () => recognitionRef.current?.stop(), []);

  const loadConvs = useCallback(() => api.get("/ai/conversations").then((r) => setConvs(r.data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/ai/attention").then((r) => setAttention(r.data)).catch(() => {});
    api.get("/platform/decisions").then((r) => setDecisions(r.data.open)).catch(() => {});
    Promise.all([api.get("/knowledge", { params: { status: "approved" } }).catch(() => ({ data: [] })), api.get("/platform/missions").catch(() => ({ data: [] })), api.get("/platform/standing-orders").catch(() => ({ data: [] }))])
      .then(([k, m, s]) => setGround({ knowledge: k.data.length, missions: m.data.filter((x) => x.status === "active").length, orders: s.data.length }));
    loadConvs();
    if (convId) api.get(`/ai/conversations/${convId}`).then((r) => { setMessages(r.data.messages || []); if (r.data.messages?.length) setInput(""); }).catch(() => { localStorage.removeItem("atlas_conv"); setConvId(null); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.tenant?.id]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, loading]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const q = location.state?.q; if (q && !firedRef.current) { firedRef.current = true; send(q); } }, [location.state]);

  const persist = async (id, msgs) => { try { await api.put(`/ai/conversations/${id}`, { messages: msgs }); loadConvs(); } catch (e) { console.error("persist conversation failed", e); } };
  const newChat = () => { setMessages([]); setConvId(null); localStorage.removeItem("atlas_conv"); setInput(STARTER); setHistOpen(false); };
  const openConv = async (c) => { const { data } = await api.get(`/ai/conversations/${c.id}`); setConvId(c.id); localStorage.setItem("atlas_conv", c.id); setMessages(data.messages || []); setInput(""); setHistOpen(false); addRecent({ type: "conversation", id: c.id, label: c.title, link: "/atlas" }); };
  const delConv = async (c) => { await api.delete(`/ai/conversations/${c.id}`); if (c.id === convId) newChat(); loadConvs(); };
  const go = (link) => { if (link) navigate(link); };

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    const id = convId || uid();
    if (!convId) { setConvId(id); localStorage.setItem("atlas_conv", id); }
    const history = messages.map((m) => ({ role: m.role, content: m.role === "assistant" ? m.answer : m.content }));
    const withUser = [...messages, { role: "user", content: q, at: new Date().toISOString() }];
    setMessages(withUser); setLoading(true);
    let reply;
    try { const { data } = await api.post("/ai/ask", { question: q, history }); reply = { role: "assistant", answer: data.answer, citations: data.citations || [], actions: data.suggested_actions || [], assigned_to: data.assigned_to || null, at: new Date().toISOString() }; if (data.assigned_to) toast.success(`Handed to ${data.assigned_to.name}`); }
    catch (e) { reply = { role: "assistant", answer: apiError(e, "Atlas is unavailable right now."), citations: [], actions: [] }; }
    finally { setLoading(false); }
    const all = [...withUser, reply]; setMessages(all); persist(id, all);
  };
  const runAction = async (a, mi, ai_) => {
    try { const params = a.kind === "delete_record" ? { ...a.params, confirm: true } : a.params;
      const { data } = await api.post("/ai/act", { kind: a.kind, params, label: a.label }); toast.success(a.label || "Action completed");
      setMessages((s) => { const n = s.map((m, i) => i === mi ? { ...m, actions: m.actions.map((x, j) => j === ai_ ? { ...x, done: true, affected: data.affected, skipped: data.skipped } : x) } : m); persist(convId, n); return n; }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const empty = messages.length === 0;
  const lastIdx = messages.length - 1;

  const composer = (
    <div className="w-full max-w-2xl mx-auto">
      <div className="relative rounded-2xl border bg-white shadow-sm focus-within:shadow-md transition-shadow" style={{ borderColor: "var(--line)" }}>
        <Textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onFocus={(e) => { if (empty && input === STARTER) e.target.select(); }}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          enterKeyHint="send" inputMode="text"
          placeholder={`Ask Atlas about ${activeTenant?.name}…`} rows={empty ? 3 : 1}
          className={`border-0 shadow-none focus-visible:ring-0 resize-none bg-transparent pr-[6.5rem] ${empty ? "min-h-[96px] text-lg sm:text-xl leading-snug p-4" : "min-h-[52px] max-h-40 text-base p-3.5"}`} data-testid="ai-input" />
        {speechSupported && <Button size="icon" variant="ghost" className={`absolute right-14 bottom-2.5 h-10 w-10 rounded-full transition-colors ${listening ? "bg-red-50 text-red-600" : "text-slate-400 hover:text-slate-700"}`} onClick={toggleVoice} disabled={loading} title={listening ? "Stop listening" : "Speak your question"} data-testid="ai-voice"><Mic className={`w-5 h-5 ${listening ? "animate-pulse" : ""}`} /></Button>}
        <Button size="icon" className="absolute right-2.5 bottom-2.5 h-10 w-10 rounded-full text-white transition-transform hover:-translate-y-px" style={{ background: "var(--ai)" }} onClick={() => send()} disabled={loading || !input.trim()} data-testid="ai-send"><ArrowUp className="w-5 h-5" /></Button>
      </div>
      {!empty && <p className="text-[10px] text-slate-400 mt-1.5 flex items-center gap-1 px-1"><ShieldCheck className="w-3 h-3" /> Grounded in your records · permission-checked · audited</p>}
    </div>);

  return (
    <div className="h-full flex flex-col" data-testid="atlas-ai-page">
      <AtlasContextBar onHistory={() => setHistOpen(true)} onContext={() => setCtxOpen(true)} onNew={newChat} hasChat={!empty} />

      {empty ? (
        <section className="flex-1 flex flex-col items-center justify-center px-5 pb-[12vh] lg:pb-10 rise" data-testid="atlas-initial">
          <div className="w-full max-w-2xl">
            <div className="flex items-center gap-2 mb-5 sm:mb-8 justify-center"><span className="ai-dot" /><h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Atlas</h1></div>
            {composer}
            <p className="text-center text-xs text-slate-400 mt-5 flex items-center justify-center gap-1"><ShieldCheck className="w-3 h-3" /> Grounded in {activeTenant?.name} · permission-checked · audited</p>
          </div>
        </section>
      ) : (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 lg:px-8 py-5" data-testid="atlas-conversation-view">
            <div className="max-w-2xl mx-auto space-y-5">
              {messages.map((m, mi) => m.role === "user" ? (
                <div key={mi} className="flex justify-end rise"><div className="text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm max-w-[85%]" style={{ background: "var(--ink)" }} data-testid="ai-user-message">{m.content}</div></div>
              ) : (
                <div key={mi} className="flex gap-3 rise"><span className="ai-dot mt-3 flex-shrink-0" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="border rounded-2xl rounded-tl-sm p-4 space-y-3 bg-white" style={{ borderColor: "var(--line)" }} data-testid="ai-assistant-message">
                      {m.assigned_to && <AssignedBadge a={m.assigned_to} onGo={go} />}
                      <Markdown text={m.answer} />
                      {m.citations?.length > 0 && <div className="flex flex-wrap gap-1.5 pt-1"><span className="eyebrow self-center mr-1">Open</span>{m.citations.map((c, ci) => <button key={ci} onClick={() => go(c.link)} disabled={!c.link} data-testid="ai-citation" className="chip border-slate-200 text-slate-600 hover:border-slate-500 disabled:opacity-60">{c.label || c.type}{c.link && <ArrowUpRight className="w-3 h-3" />}</button>)}</div>}
                      {m.actions?.length > 0 && <div className="space-y-1.5 pt-2 hairline">{m.actions.map((a, ai_) => <ActionRow key={ai_} a={a} onRun={() => runAction(a, mi, ai_)} onGo={go} />)}</div>}
                      <div className="pt-1"><button onClick={() => { navigator.clipboard?.writeText(m.answer || ""); toast.success("Copied to clipboard"); }} data-testid="ai-copy-message" className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-700 transition-colors"><Copy className="w-3.5 h-3.5" /> Copy</button></div>
                    </div>
                    {mi === lastIdx && !loading && <div className="flex flex-wrap gap-1.5" data-testid="ai-followups">
                      <button onClick={() => send("Why? Show me the records and knowledge behind that.")} className="inline-flex items-center gap-1 rounded-full py-1.5 px-3 text-xs font-medium text-white transition-transform hover:-translate-y-px" style={{ background: "var(--ai)" }} data-testid="ai-ask-why"><HelpCircle className="w-3.5 h-3.5" /> Ask why</button>
                      {followUps(m).slice(1).map((f) => <button key={f} onClick={() => send(f)} className="chip border-slate-200 text-slate-600 hover:border-slate-500 bg-white py-1.5 px-3" data-testid="ai-followup">{f}</button>)}
                    </div>}
                  </div></div>))}
              {loading && <div className="flex items-center gap-2 text-sm text-slate-400 pl-6"><Loader2 className="w-4 h-4 animate-spin" /> Atlas is reasoning…</div>}
            </div>
          </div>
          <div className="flex-shrink-0 border-t px-4 lg:px-8 py-3" style={{ borderColor: "var(--line)", background: "var(--paper-2)" }}>{composer}</div>
        </>
      )}

      <HistorySheet open={histOpen} onOpenChange={setHistOpen} convs={convs} convId={convId} onOpen={openConv} onDelete={delConv} onNew={newChat} />
      <ContextSheet open={ctxOpen} onOpenChange={setCtxOpen} tenant={activeTenant} ctx={ctx} ground={ground} decisions={decisions} attention={attention} onAsk={send} />
    </div>);
}
